import React from 'react';
import { Icon } from '@iconify/react';
import { ToastContainer, toast } from 'react-toastify';
import '../../styles/app-toast.scss';

export const TOAST_TYPES = {
  success: {
    icon: 'mdi:check-circle-outline',
    className: 'app-toast app-toast--success',
    method: 'success',
  },
  error: {
    icon: 'mdi:alert-circle-outline',
    className: 'app-toast app-toast--error',
    method: 'error',
  },
  warning: {
    icon: 'mdi:alert-outline',
    className: 'app-toast app-toast--warning',
    method: 'warning',
  },
  info: {
    icon: 'mdi:information-outline',
    className: 'app-toast app-toast--info',
    method: 'info',
  },
  loading: {
    icon: 'mdi:loading',
    className: 'app-toast app-toast--loading',
    method: 'info',
  },
};

/** Alias métier → type toast */
const TYPE_ALIASES = {
  validate: 'success',
  validated: 'success',
  ok: 'success',
  danger: 'error',
  fail: 'error',
  warn: 'warning',
  progress: 'loading',
  pending: 'loading',
};

export const DEFAULT_TOAST_OPTIONS = {
  position: 'top-right',
  autoClose: 4500,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
};

const LOADING_TOAST_OPTIONS = {
  autoClose: false,
  closeOnClick: false,
  draggable: false,
  hideProgressBar: true,
};

export function normalizeToastType(type = 'info') {
  const key = String(type || 'info').toLowerCase();
  return TYPE_ALIASES[key] || (TOAST_TYPES[key] ? key : 'info');
}

/**
 * Contenu visuel d'une notification (icône + message).
 */
export function AppToastContent({ message, type = 'info' }) {
  const resolved = normalizeToastType(type);
  const meta = TOAST_TYPES[resolved] || TOAST_TYPES.info;
  const iconClass = resolved === 'loading'
    ? 'app-toast__icon app-toast__icon--spin'
    : 'app-toast__icon';

  return (
    <div className="app-toast__body">
      <Icon icon={meta.icon} className={iconClass} aria-hidden />
      <span className="app-toast__message">{message}</span>
    </div>
  );
}

/**
 * Conteneur global à monter une seule fois (ex. dans App.js).
 */
export function AppToastContainer() {
  return (
    <ToastContainer
      position={DEFAULT_TOAST_OPTIONS.position}
      autoClose={DEFAULT_TOAST_OPTIONS.autoClose}
      hideProgressBar={DEFAULT_TOAST_OPTIONS.hideProgressBar}
      newestOnTop
      closeOnClick={DEFAULT_TOAST_OPTIONS.closeOnClick}
      pauseOnHover={DEFAULT_TOAST_OPTIONS.pauseOnHover}
      draggable={DEFAULT_TOAST_OPTIONS.draggable}
      theme="colored"
      icon={false}
      style={{ zIndex: 20000 }}
      toastClassName="app-toast"
      bodyClassName="app-toast__inner"
      progressClassName="app-toast__progress"
    />
  );
}

/**
 * Affiche une notification toast.
 * @param {string} message
 * @param {'success'|'error'|'warning'|'info'|'loading'|'validate'} [type='info']
 * @param {object} [options] options react-toastify
 */
export function showAppToast(message, type = 'info', options = {}) {
  if (!message) return null;

  const resolved = normalizeToastType(type);
  const meta = TOAST_TYPES[resolved] || TOAST_TYPES.info;
  const method = toast[meta.method] || toast.info;
  const defaults = resolved === 'loading'
    ? { ...DEFAULT_TOAST_OPTIONS, ...LOADING_TOAST_OPTIONS }
    : DEFAULT_TOAST_OPTIONS;

  return method(
    <AppToastContent message={message} type={resolved} />,
    {
      ...defaults,
      ...options,
      icon: false,
      className: `${meta.className}${options.className ? ` ${options.className}` : ''}`,
    },
  );
}

/**
 * Exécute une action async avec toast "en cours" → succès / erreur.
 * @template T
 * @param {(() => Promise<T>)|Promise<T>} action
 * @param {{ loading?: string, success?: string|false|((result: T) => string), error?: string|((err: unknown) => string) }} [messages]
 * @returns {Promise<T>}
 */
export async function runWithToast(action, messages = {}) {
  const {
    loading = 'Traitement en cours…',
    success = 'Terminé',
    error,
  } = messages;

  const toastId = showAppToast(loading, 'loading');
  try {
    const result = typeof action === 'function' ? await action() : await action;
    toast.dismiss(toastId);
    if (success !== false && success != null) {
      const successMessage = typeof success === 'function' ? success(result) : success;
      if (successMessage) {
        showAppToast(successMessage, 'success');
      }
    }
    return result;
  } catch (err) {
    toast.dismiss(toastId);
    const errorMessage = typeof error === 'function'
      ? error(err)
      : (error || err?.message || 'Une erreur est survenue');
    if (errorMessage) {
      showAppToast(errorMessage, 'error');
    }
    throw err;
  }
}

/** API courte pour usage dans les pages. */
export const notify = {
  show: showAppToast,
  success: (message, options) => showAppToast(message, 'success', options),
  error: (message, options) => showAppToast(message, 'error', options),
  warning: (message, options) => showAppToast(message, 'warning', options),
  info: (message, options) => showAppToast(message, 'info', options),
  loading: (message, options) => showAppToast(message, 'loading', options),
  validate: (message, options) => showAppToast(message, 'success', options),
  run: runWithToast,
  dismiss: (id) => toast.dismiss(id),
  dismissAll: () => toast.dismiss(),
};

export default notify;
